import type { BlurViewProps } from '@sbaiahmed1/react-native-blur';
import { ApplicationType } from '@unifyapps/defs/types/interface';
import type { LinearGradientProps } from 'expo-linear-gradient';
import { useMemo } from 'react';
import { useInterfaceStore } from '../../no-code/stores/InterfaceStore';
import { safeJsonParse } from '../json';
import type { KeyboardAvoidingViewProps } from './types';

type UseCustomStylesProps = {
  customCSS: string | undefined;
  style?: object | undefined;
  customStyles: object | undefined;
};

type UseCustomStylesReturn = {
  gradientConfig: LinearGradientProps | undefined;
  finalStyle: object | undefined;
  keyboardConfig?: KeyboardAvoidingViewProps;
  blurConfig?: BlurViewProps;
};

/**
 * Converts numeric values to pixel units for web compatibility.
 *
 * Special handling for lineHeight:
 * - In React Native: lineHeight works correctly as a number
 * - In Web: lineHeight:number gets interpreted as line-height: `(number*font-size)`px
 * - So here converting lineHeight values to string with 'px' suffix for web
 *
 * This ensures consistent behavior across platforms where lineHeight
 * needs to be explicitly specified as a pixel value on web.
 */
const convertNumToPx = (obj: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, key === 'lineHeight' ? `${value}px` : value]),
  );

export default function useCustomStyles({
  customCSS,
  style,
  customStyles,
}: UseCustomStylesProps): UseCustomStylesReturn {
  const applicationType = useInterfaceStore().hooks.record.useApplicationType();
  const isMobile = applicationType === ApplicationType.MOBILE;
  const shouldUseParsedCustomStyles = Boolean(customStyles) || (Boolean(customCSS) && isMobile);

  return useMemo(() => {
    if (!shouldUseParsedCustomStyles) {
      return {
        gradientConfig: undefined,
        blurConfig: undefined,
        keyboardConfig: undefined,
        finalStyle: style,
      };
    }

    // On web we should only consume already-parsed customStyles. Raw customCSS for web
    // is still standard CSS and must continue to be handled by the existing CSS pipeline.
    const parsedStyles = (customStyles ?? safeJsonParse(customCSS ?? '', {})) as {
      linearGradient?: LinearGradientProps;
      blurConfig?: BlurViewProps;
      [key: string]: LinearGradientProps | object | undefined | string | number | boolean;
    };

    const linearGradient =
      parsedStyles.linearGradient && typeof parsedStyles.linearGradient === 'object'
        ? parsedStyles.linearGradient
        : undefined;

    const keyboardConfig =
      parsedStyles.keyboardConfig && typeof parsedStyles.keyboardConfig === 'object'
        ? parsedStyles.keyboardConfig
        : undefined;

    const { linearGradient: _, blurConfig, style: nestedStyle, ...restStyles } = parsedStyles;

    // some sheets author custom styles nested under a `style` key (e.g. { style: { flex: 1 } });
    // flatten it alongside the top-level props so those styles actually apply.
    const flatStyles =
      nestedStyle && typeof nestedStyle === 'object'
        ? { ...restStyles, ...(nestedStyle as object) }
        : restStyles;
    const pxRestStyles = convertNumToPx(flatStyles);
    const finalStyle = { ...style, ...pxRestStyles };
    return {
      gradientConfig: linearGradient,
      keyboardConfig,
      blurConfig,
      finalStyle,
    };
  }, [customCSS, style, customStyles, shouldUseParsedCustomStyles]);
}
